import { getMetaBrowserIds } from './metaPixel';

const API_BASE =
  import.meta.env.VITE_VENDAS_IA_API?.replace(/\/$/, '') || '/vendas/api/public';

export const CINTA_CART_STORAGE_KEY = 'lunins_cinta_cart_id_v1';

export type CintaProduct = {
  name: string;
  priceFullCents: number;
  priceActionCents: number;
  installmentCount: number;
  installmentValueCents: number;
  priceFullLabel: string;
  priceActionLabel: string;
  installmentLabel: string;
  campaign: string;
  benefits: string[];
};

export type CepLookup = {
  cep: string;
  logradouro: string;
  bairro: string;
  municipio: string;
  uf: string;
};

export type CartUpsertPayload = {
  cartId?: string;
  name?: string;
  phone?: string;
  email?: string;
  step: 'contact' | 'fiscal' | 'review';
  fiscal?: {
    cpfCnpj?: string;
    razaoSocial?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    municipio?: string;
    uf?: string;
    email?: string;
  };
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  fbp?: string;
  fbc?: string;
};

function metaTrackingFields(): { fbp?: string; fbc?: string } {
  return getMetaBrowserIds();
}

function currentUtm() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    content: params.get('utm_content') || undefined,
    term: params.get('utm_term') || undefined,
  };
}

async function parseJson<T>(res: Response): Promise<T & { ok?: boolean; error?: string }> {
  const data = (await res.json()) as T & { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || `Erro HTTP ${res.status}`);
  }
  return data;
}

export async function fetchCintaProduct(): Promise<CintaProduct> {
  const res = await fetch(`${API_BASE}/cinta/product`);
  return parseJson<CintaProduct>(res);
}

export async function lookupCep(cep: string): Promise<CepLookup> {
  const digits = cep.replace(/\D/g, '');
  const res = await fetch(`${API_BASE}/cinta/cep/${digits}`);
  return parseJson<CepLookup>(res);
}

export async function upsertCintaCart(payload: CartUpsertPayload) {
  const res = await fetch(`${API_BASE}/cinta/cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      utm: payload.utm ?? currentUtm(),
      ...metaTrackingFields(),
      ...(payload.fbp ? { fbp: payload.fbp } : {}),
      ...(payload.fbc ? { fbc: payload.fbc } : {}),
    }),
  });
  const data = await parseJson<{
    cartId: string;
    leadId: string;
    conversationId: string;
    step: string;
    fiscalComplete: boolean;
    missingFiscal: string[];
  }>(res);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CINTA_CART_STORAGE_KEY, data.cartId);
  }
  return data;
}

export async function checkoutCintaCart(params: {
  cartId: string;
  fiscal: NonNullable<CartUpsertPayload['fiscal']> & {
    cpfCnpj: string;
    razaoSocial: string;
    cep: string;
    logradouro: string;
    numero: string;
    bairro: string;
    municipio: string;
    uf: string;
  };
}) {
  const res = await fetch(`${API_BASE}/cinta/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...params, ...metaTrackingFields() }),
  });
  return parseJson<{ checkoutUrl: string; cartId: string; stub?: boolean }>(res);
}

export function normalizePhoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) return digits;
  return digits.slice(-11);
}

export function formatPhoneDisplay(value: string): string {
  const d = normalizePhoneDigits(value);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return formatPhoneDisplay(d.slice(-11));
}

export function formatCpfCnpj(value: string): string {
  const d = value.replace(/\D/g, '');
  if (d.length <= 11) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
  }
  if (d.length <= 14) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
  }
  return formatCpfCnpj(d.slice(0, 14));
}

export function formatCep(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}
